import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import { FaPrint, FaDownload } from "react-icons/fa";
import axiosInstance from "../config/axios";
import { buildCsv, downloadCsvFile } from "../utils/csvDownload";
import {
  PENDING_TRADE_STATUSES,
  PENDING_TRADE_SORT_OPTIONS,
  PENDING_TRADE_CLASSIFICATIONS,
  PENDING_TRADE_PROPERTY_TYPES,
  buildPendingTradeReport,
  formatAgentSortName,
  formatReportMoney,
} from "../utils/pendingTradeReportUtils";
import { useRole } from "../context/RoleContext";
import { resolveLoggedInAgentId } from "../utils/agentReportUtils";
import logo from "../Assets/logo3.jpg";

const COMPANY = "ABC Realty Inc., Brokerage";
const REPORT_TITLE = "Pending Trades Report";

const FinancialReportsPendingTrades = () => {
  const { pathname } = useLocation();
  const isFinancePage = pathname === "/finance-pending-trade-report";
  const { isAgent, appUser } = useRole();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("ALL");
  const [sortBy, setSortBy] = useState("offerDate");
  const [classification, setClassification] = useState("ALL");
  const [propertyType, setPropertyType] = useState("ALL");
  const [agentId, setAgentId] = useState("");
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [agentGroups, setAgentGroups] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const res = await axiosInstance.get("/agents");
        const sorted = (res.data || []).slice().sort((a, b) => {
          const empA = Number(a.employeeNo) || 0;
          const empB = Number(b.employeeNo) || 0;
          if (empA !== empB) return empA - empB;
          return formatAgentSortName(a).localeCompare(
            formatAgentSortName(b),
            undefined,
            { sensitivity: "base" },
          );
        });
        setAgents(sorted);
      } catch (err) {
        console.error(err);
      }
    };
    loadAgents();
  }, []);

  useEffect(() => {
    if (!isAgent) return;
    const id = resolveLoggedInAgentId(appUser, agents);
    if (id) setAgentId(id);
  }, [isAgent, appUser, agents]);

  const selectedAgentLabel = () => {
    if (isAgent && appUser?.displayName) return appUser.displayName;
    if (!agentId) return "All Agents";
    const a = agents.find((x) => String(x._id) === String(agentId));
    if (!a) return "All Agents";
    const emp = a.employeeNo != null ? `${a.employeeNo} - ` : "";
    return `${emp}${formatAgentSortName(a)}`;
  };

  const labelFor = (options, value) =>
    options.find((o) => o.value === value)?.label || value;

  const formatPeriod = () => {
    if (!fromDate || !toDate) return "";
    const fmt = (iso) => {
      const [y, m, d] = iso.split("-").map(Number);
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return `${months[m - 1]} ${String(d).padStart(2, "0")}, ${y}`;
    };
    return `From ${fmt(fromDate)} To ${fmt(toDate)}`;
  };

  const handleGenerate = async () => {
    if (!fromDate || !toDate) {
      setError("Please select both Start and End dates.");
      return;
    }
    if (fromDate > toDate) {
      setError("Start date must be on or before End date.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/trades");
      const scopedAgentId = isAgent
        ? resolveLoggedInAgentId(appUser, agents) || agentId
        : agentId;
      const result = buildPendingTradeReport(res.data || [], agents, {
        fromDate,
        toDate,
        agentId: scopedAgentId,
        status,
        classification,
        propertyType,
        sortBy,
      });
      setAgentGroups(result.agentGroups);
      setSummary(result.summary);
      setShowReport(true);
    } catch (err) {
      console.error(err);
      setError("Failed to load trades.");
      setAgentGroups([]);
      setSummary(null);
      setShowReport(false);
    } finally {
      setLoading(false);
    }
  };

  const renderSectionRows = (rows) =>
    rows.map((r, idx) => (
      <tr key={`${r.tradeNumber}-${r.endSide}-${idx}`}>
        <td className="border px-2 py-1">{r.tradeNumber}</td>
        <td className="border px-2 py-1 text-center">{r.type}</td>
        <td className="border px-2 py-1">{r.address}</td>
        <td className="border px-2 py-1 text-center">{r.firmCond}</td>
        <td className="border px-2 py-1">{r.listingNumber}</td>
        <td className="border px-2 py-1">{r.offerDate}</td>
        <td className="border px-2 py-1">{r.closeDate}</td>
        <td className="border px-2 py-1 text-right">
          {r.pctOfEnd != null ? Number(r.pctOfEnd).toFixed(2) : ""}
        </td>
        <td className="border px-2 py-1 text-right">
          {Number(r.ends || 0).toFixed(2)}
        </td>
        <td className="border px-2 py-1 text-right">
          {formatReportMoney(r.saleVolume)}
        </td>
        <td className="border px-2 py-1 text-right">
          {formatReportMoney(r.commission)}
        </td>
        <td className="border px-2 py-1 text-right">
          {r.fees ? formatReportMoney(r.fees) : ""}
        </td>
        <td className="border px-2 py-1 text-right">
          {r.payouts ? formatReportMoney(r.payouts) : ""}
        </td>
        <td className="border px-2 py-1 text-right">
          {formatReportMoney(r.agentNet)}
        </td>
      </tr>
    ));

  const openPrintWindow = () => {
    if (!showReport) return;

    const safe = (v) =>
      String(v ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;");
    const money = (n) => formatReportMoney(n);

    const sectionHtml = (title, rows, totals) => {
      if (!rows.length) return "";
      const body = rows
        .map(
          (r) => `
        <tr>
          <td>${safe(r.tradeNumber)}</td>
          <td class="ctr">${safe(r.type)}</td>
          <td>${safe(r.address)}</td>
          <td class="ctr">${safe(r.firmCond)}</td>
          <td>${safe(r.listingNumber)}</td>
          <td>${safe(r.offerDate)}</td>
          <td>${safe(r.closeDate)}</td>
          <td class="num">${r.pctOfEnd != null ? Number(r.pctOfEnd).toFixed(2) : ""}</td>
          <td class="num">${Number(r.ends || 0).toFixed(2)}</td>
          <td class="num">${money(r.saleVolume)}</td>
          <td class="num">${money(r.commission)}</td>
          <td class="num">${r.fees ? money(r.fees) : ""}</td>
          <td class="num">${r.payouts ? money(r.payouts) : ""}</td>
          <td class="num">${money(r.agentNet)}</td>
        </tr>`,
        )
        .join("");
      return `
        <tr class="sec-hdr"><td colspan="14">${safe(title)}</td></tr>
        ${body}
        <tr class="subtot">
          <td colspan="7">Total # of ${safe(title)}s: ${totals.count.toFixed(2)}</td>
          <td></td>
          <td class="num">${Number(totals.ends || 0).toFixed(2)}</td>
          <td class="num">${money(totals.saleVolume)}</td>
          <td class="num">${money(totals.commission)}</td>
          <td class="num">${totals.fees ? money(totals.fees) : ""}</td>
          <td class="num">${totals.payouts ? money(totals.payouts) : ""}</td>
          <td class="num">${money(totals.agentNet)}</td>
        </tr>`;
    };

    const groupsHtml =
      agentGroups.length === 0
        ? `<tr><td colspan="14" style="text-align:center;padding:8px;">No pending trades for this selection.</td></tr>`
        : agentGroups
            .map((g) => {
              return `
              <tr class="agent-hdr"><td colspan="14">${safe(g.groupLabel)}</td></tr>
              ${sectionHtml("Listing End", g.listingRows, g.listingTotals)}
              ${sectionHtml("Selling End", g.sellingRows, g.sellingTotals)}
            `;
            })
            .join("");

    const totalsHtml = summary
      ? `
        <tr class="grand">
          <td colspan="7">Grand Total (${summary.tradeCount} trade(s))</td>
          <td></td>
          <td class="num">${Number(summary.ends || 0).toFixed(2)}</td>
          <td class="num">${money(summary.saleVolume)}</td>
          <td class="num">${money(summary.commission)}</td>
          <td class="num">${summary.fees ? money(summary.fees) : ""}</td>
          <td class="num">${summary.payouts ? money(summary.payouts) : ""}</td>
          <td class="num">${money(summary.agentNet)}</td>
        </tr>`
      : "";

    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return;

    w.document.write(`
      <html>
        <head>
          <title></title>
          <style>
            @page { size: portrait; margin: 0; }
            html, body { margin: 0; padding: 0; }
            body {
              font-family: Arial, sans-serif;
              font-size: 8.5px;
              color: #111;
              padding: 10mm;
              box-sizing: border-box;
            }
            .header { text-align: center; margin-bottom: 10px; }
            .header .company { font-weight: 700; font-size: 12px; }
            .header .title { font-weight: 700; font-size: 12px; margin-top: 2px; }
            .header .meta { font-size: 9px; margin-top: 2px; }
            .logo { max-height: 40px; display:block; margin: 0 auto 6px; }
            table { width: 100%; border-collapse: collapse; font-size: 7.5px; }
            th, td { border: 1px solid #999; padding: 2px 3px; vertical-align: top; }
            th { background: #f2f2f2; text-align: center; font-size: 7px; }
            td.num, th.num { text-align: right; white-space: nowrap; }
            td.ctr { text-align: center; }
            tr.agent-hdr td { font-weight: 700; background: #e8eef6; }
            tr.sec-hdr td { font-weight: 700; background: #f3f4f6; }
            tr.subtot td { font-weight: 600; background: #fafafa; }
            tr.grand td { font-weight: 700; background: #f3f4f6; }
            .print-btn { margin-top: 12px; display:block; margin-left:auto; margin-right:auto; }
            @media print {
              .print-btn { display:none !important; }
              body { padding: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img class="logo" src="${logo}" alt="" />
            <div class="company">${safe(COMPANY)}</div>
            <div class="title">${safe(REPORT_TITLE)}</div>
            <div class="meta">For All Trades</div>
            <div class="meta">Agent: ${safe(selectedAgentLabel())}</div>
            <div class="meta">${safe(formatPeriod())}</div>
            <div class="meta">
              Status: ${safe(labelFor(PENDING_TRADE_STATUSES, status))} ·
              Classification: ${safe(labelFor(PENDING_TRADE_CLASSIFICATIONS, classification))} ·
              Property Type: ${safe(labelFor(PENDING_TRADE_PROPERTY_TYPES, propertyType))} ·
              Sorted by: ${safe(labelFor(PENDING_TRADE_SORT_OPTIONS, sortBy))}
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Trade #</th>
                <th>Type</th>
                <th>Address</th>
                <th>Firm/Cond</th>
                <th>Listing #</th>
                <th>Offer Date</th>
                <th>Close Date</th>
                <th>% of End</th>
                <th>Ends</th>
                <th>Sale Volume</th>
                <th>Commission</th>
                <th>Fees</th>
                <th>Payouts</th>
                <th>Agent Net</th>
              </tr>
            </thead>
            <tbody>
              ${groupsHtml}
              ${totalsHtml}
            </tbody>
          </table>
          <button class="print-btn" onclick="window.print()">Print</button>
        </body>
      </html>
    `);
    w.document.close();
    try {
      w.document.title = " ";
    } catch (_) {
      /* ignore */
    }
    w.focus();
  };

  const handleDownloadCsv = () => {
    if (!agentGroups.length) return;
    const headers = [
      "Agent",
      "End Side",
      "Trade #",
      "Type",
      "Address",
      "Firm/Cond",
      "Listing #",
      "Offer Date",
      "Close Date",
      "% of End",
      "Ends",
      "Sale Volume",
      "Commission",
      "Fees",
      "Payouts",
      "Agent Net",
    ];
    const rows = [];
    for (const g of agentGroups) {
      for (const r of [...g.listingRows, ...g.sellingRows]) {
        rows.push([
          g.groupLabel,
          r.endSide === "selling" ? "Selling End" : "Listing End",
          r.tradeNumber,
          r.type,
          r.address,
          r.firmCond,
          r.listingNumber,
          r.offerDate,
          r.closeDate,
          r.pctOfEnd != null ? Number(r.pctOfEnd).toFixed(2) : "",
          Number(r.ends || 0).toFixed(2),
          Number(r.saleVolume || 0).toFixed(2),
          Number(r.commission || 0).toFixed(2),
          Number(r.fees || 0).toFixed(2),
          Number(r.payouts || 0).toFixed(2),
          Number(r.agentNet || 0).toFixed(2),
        ]);
      }
    }
    if (summary) {
      rows.push([]);
      rows.push([
        "TOTALS",
        "",
        `Trades: ${summary.tradeCount}`,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        Number(summary.ends || 0).toFixed(2),
        Number(summary.saleVolume || 0).toFixed(2),
        Number(summary.commission || 0).toFixed(2),
        Number(summary.fees || 0).toFixed(2),
        Number(summary.payouts || 0).toFixed(2),
        Number(summary.agentNet || 0).toFixed(2),
      ]);
    }
    const csv = buildCsv(headers, rows);
    downloadCsvFile(`pending-trade-report-${fromDate}-to-${toDate}.csv`, csv);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex flex-1">
        <div className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center mb-6 no-print">
              <h1 className="text-2xl font-bold text-gray-900">
                Pending Trade Report
              </h1>
            </div>
            {isAgent ? (
              <p className="text-sm text-gray-600 mb-4 no-print">
                Showing pending trades for your account only.
              </p>
            ) : null}

            <div className="bg-white p-6 rounded-lg shadow-md mb-6 no-print">
              <p className="text-sm text-gray-600 mb-4">
                Pending trades by listing/selling end. Date range uses the
                selected Sort By field.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  >
                    {PENDING_TRADE_STATUSES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  >
                    {PENDING_TRADE_SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Classification
                  </label>
                  <select
                    value={classification}
                    onChange={(e) => setClassification(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  >
                    {PENDING_TRADE_CLASSIFICATIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property Type
                  </label>
                  <select
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full"
                  >
                    {PENDING_TRADE_PROPERTY_TYPES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agent
                  </label>
                  {isAgent ? (
                    <div className="border border-gray-200 bg-gray-50 rounded px-3 py-2 w-full text-gray-800">
                      {selectedAgentLabel()}
                    </div>
                  ) : (
                    <select
                      value={agentId}
                      onChange={(e) => setAgentId(e.target.value)}
                      className="border border-gray-300 rounded px-3 py-2 w-full"
                    >
                      <option value="">All Agents</option>
                      {agents.map((a) => (
                        <option key={a._id} value={a._id}>
                          {a.employeeNo != null ? `${a.employeeNo} — ` : ""}
                          {formatAgentSortName(a)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={loading}
                    className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-60"
                  >
                    {loading ? "Loading…" : "Generate Report"}
                  </button>
                  {showReport && agentGroups.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={openPrintWindow}
                        className="flex items-center gap-2 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                      >
                        <FaPrint /> Print
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadCsv}
                        className="flex items-center gap-2 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                      >
                        <FaDownload /> CSV
                      </button>
                    </>
                  )}
                </div>
              </div>
              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>

            {showReport && (
              <div className="bg-white p-6 rounded-lg shadow-md overflow-x-auto">
                {agentGroups.length === 0 ? (
                  <p className="text-gray-600">
                    No pending trades found for this selection.
                  </p>
                ) : (
                  <table className="min-w-full border border-gray-300 text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border px-2 py-1 text-left">Trade #</th>
                        <th className="border px-2 py-1 text-center">Type</th>
                        <th className="border px-2 py-1 text-left">Address</th>
                        <th className="border px-2 py-1 text-center">
                          Firm/Cond
                        </th>
                        <th className="border px-2 py-1 text-left">
                          Listing #
                        </th>
                        <th className="border px-2 py-1 text-left">
                          Offer Date
                        </th>
                        <th className="border px-2 py-1 text-left">
                          Close Date
                        </th>
                        <th className="border px-2 py-1 text-right">
                          % of End
                        </th>
                        <th className="border px-2 py-1 text-right">Ends</th>
                        <th className="border px-2 py-1 text-right">
                          Sale Volume
                        </th>
                        <th className="border px-2 py-1 text-right">
                          Commission
                        </th>
                        <th className="border px-2 py-1 text-right">Fees</th>
                        <th className="border px-2 py-1 text-right">Payouts</th>
                        <th className="border px-2 py-1 text-right">
                          Agent Net
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentGroups.map((g) => (
                        <React.Fragment key={g.groupKey}>
                          <tr className="bg-blue-50">
                            <td
                              colSpan={14}
                              className="border px-2 py-1 font-semibold"
                            >
                              {g.groupLabel}
                            </td>
                          </tr>
                          {g.listingRows.length > 0 && (
                            <>
                              <tr className="bg-gray-50">
                                <td
                                  colSpan={14}
                                  className="border px-2 py-1 font-medium"
                                >
                                  Listing End
                                </td>
                              </tr>
                              {renderSectionRows(g.listingRows)}
                              <tr className="bg-gray-50 font-medium">
                                <td className="border px-2 py-1" colSpan={7}>
                                  Total # of Listing Ends:{" "}
                                  {g.listingTotals.count.toFixed(2)}
                                </td>
                                <td className="border px-2 py-1" />
                                <td className="border px-2 py-1 text-right">
                                  {g.listingTotals.ends.toFixed(2)}
                                </td>
                                <td className="border px-2 py-1 text-right">
                                  {formatReportMoney(g.listingTotals.saleVolume)}
                                </td>
                                <td className="border px-2 py-1 text-right">
                                  {formatReportMoney(g.listingTotals.commission)}
                                </td>
                                <td className="border px-2 py-1 text-right">
                                  {g.listingTotals.fees
                                    ? formatReportMoney(g.listingTotals.fees)
                                    : ""}
                                </td>
                                <td className="border px-2 py-1 text-right">
                                  {g.listingTotals.payouts
                                    ? formatReportMoney(g.listingTotals.payouts)
                                    : ""}
                                </td>
                                <td className="border px-2 py-1 text-right">
                                  {formatReportMoney(g.listingTotals.agentNet)}
                                </td>
                              </tr>
                            </>
                          )}
                          {g.sellingRows.length > 0 && (
                            <>
                              <tr className="bg-gray-50">
                                <td
                                  colSpan={14}
                                  className="border px-2 py-1 font-medium"
                                >
                                  Selling End
                                </td>
                              </tr>
                              {renderSectionRows(g.sellingRows)}
                              <tr className="bg-gray-50 font-medium">
                                <td className="border px-2 py-1" colSpan={7}>
                                  Total # of Selling Ends:{" "}
                                  {g.sellingTotals.count.toFixed(2)}
                                </td>
                                <td className="border px-2 py-1" />
                                <td className="border px-2 py-1 text-right">
                                  {g.sellingTotals.ends.toFixed(2)}
                                </td>
                                <td className="border px-2 py-1 text-right">
                                  {formatReportMoney(
                                    g.sellingTotals.saleVolume,
                                  )}
                                </td>
                                <td className="border px-2 py-1 text-right">
                                  {formatReportMoney(
                                    g.sellingTotals.commission,
                                  )}
                                </td>
                                <td className="border px-2 py-1 text-right">
                                  {g.sellingTotals.fees
                                    ? formatReportMoney(g.sellingTotals.fees)
                                    : ""}
                                </td>
                                <td className="border px-2 py-1 text-right">
                                  {g.sellingTotals.payouts
                                    ? formatReportMoney(g.sellingTotals.payouts)
                                    : ""}
                                </td>
                                <td className="border px-2 py-1 text-right">
                                  {formatReportMoney(g.sellingTotals.agentNet)}
                                </td>
                              </tr>
                            </>
                          )}
                        </React.Fragment>
                      ))}
                      {summary && (
                        <tr className="font-bold bg-gray-100">
                          <td className="border px-2 py-2" colSpan={7}>
                            Grand Total ({summary.tradeCount} trade
                            {summary.tradeCount === 1 ? "" : "s"})
                          </td>
                          <td className="border px-2 py-2" />
                          <td className="border px-2 py-2 text-right">
                            {summary.ends.toFixed(2)}
                          </td>
                          <td className="border px-2 py-2 text-right">
                            {formatReportMoney(summary.saleVolume)}
                          </td>
                          <td className="border px-2 py-2 text-right">
                            {formatReportMoney(summary.commission)}
                          </td>
                          <td className="border px-2 py-2 text-right">
                            {summary.fees
                              ? formatReportMoney(summary.fees)
                              : ""}
                          </td>
                          <td className="border px-2 py-2 text-right">
                            {summary.payouts
                              ? formatReportMoney(summary.payouts)
                              : ""}
                          </td>
                          <td className="border px-2 py-2 text-right">
                            {formatReportMoney(summary.agentNet)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialReportsPendingTrades;
